import { Injectable } from '@angular/core';
import { ChapterInfo, DocumentInfo, UiWidget } from '@pt/pt-models';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { BehaviorSubject } from 'rxjs';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';
import { Widget } from 'it/primatoo/openapi/api/org/schemas/common/docs/Widget';

@Injectable({
  providedIn: 'root'
})
export class EditorStoreService {


  private _currentActiveChapterWidgets: UiWidget[] = [];
  private _disableWarning: TreatmentWarning[] = [];
  private _activeChapters: ChapterSelector[] = [];

  private _chapterInfo: ChapterInfo;
  private chapterInfoSubject = new BehaviorSubject<ChapterInfo>(undefined);
  public chapterInfo$ = this.chapterInfoSubject.asObservable();

  private _document: DocumentInfo;
  private docInfoSubject = new BehaviorSubject<DocumentInfo>(undefined);


  static getChapterSelectorAlias(selector: ChapterSelector): string {
    switch (selector) {
      case ChapterSelector.TREAT_CHECK:
        return 'check';
      case ChapterSelector.TREAT_DPIA:
        return 'dpia';
      case ChapterSelector.TREAT_RA:
        return 'ra';
      case ChapterSelector.TREAT_TREAT:
        return 'tr';
      case ChapterSelector.STANDARD:
        return 'st';
      case ChapterSelector.TREAT_LIA:
        return 'lia';
      case ChapterSelector.BASIC:
      default:
        return 'basic';
    }
  }

  static getChapterSelectorFromAlias(alias: string): ChapterSelector | undefined {
    switch (alias) {
      case 'check':
        return ChapterSelector.TREAT_CHECK;
      case 'dpia':
        return ChapterSelector.TREAT_DPIA;
      case 'ra':
        return ChapterSelector.TREAT_RA;
      case 'tr':
        return ChapterSelector.TREAT_TREAT;
      case 'st':
        return ChapterSelector.STANDARD;
      case 'basic':
        return ChapterSelector.BASIC;
      case 'lia':
        return ChapterSelector.TREAT_LIA;
      default:
        return undefined;
    }
  }

  get currentActiveChapterWidgets(): UiWidget[] {
    return this._currentActiveChapterWidgets;
  }

  public setWidgets(widgets: UiWidget[]): void {
    this._currentActiveChapterWidgets = JSON.parse(JSON.stringify(widgets));
  }

  public upsertWidget(widget: UiWidget): void {
    const newWidgets: UiWidget[] = [];
    let updateWidget = false;
    this._currentActiveChapterWidgets.forEach(w => {
      if (w.internalId === widget.internalId) {
        updateWidget = true;
        newWidgets.push(widget);
      } else {
        newWidgets.push(w);
      }
    });
    if (!updateWidget) {
      newWidgets.push(widget);
    }
    this._currentActiveChapterWidgets = JSON.parse(JSON.stringify(newWidgets));
  }

  public getWidget(widgetInternalId: number): UiWidget | undefined {
    return this._currentActiveChapterWidgets.find((w) => w.internalId === widgetInternalId);

  }

  public upsertWidgetFromRequest(request: UpsertWidgetsRequest) {

    const upsertWidgetForLog: UiWidget[] = [];
    const newWidget: UiWidget[] = [];
    this._currentActiveChapterWidgets.forEach((w) => {
      const updatedWidget: Widget = request.widgets.find((uw) => uw.internalId === w.internalId);
      if (updatedWidget) {
        const upsw: UiWidget = {
          ...w,
          value: updatedWidget.value,
          properties: updatedWidget.properties
        };
        newWidget.push(upsw);
        upsertWidgetForLog.push(upsw);
      } else {
        newWidget.push(w);
      }
    });
    this._currentActiveChapterWidgets = JSON.parse(JSON.stringify(newWidget));

  }

  public activeChapter(chapter: ChapterSelector) {
    if (!this._activeChapters.includes(chapter)) {
      this._activeChapters.push(chapter);
    }
  }

  public setDisableWarning(warning: TreatmentWarning) {
    if (!this._disableWarning.includes(warning)) {
      this._disableWarning.push(warning);
    }
  }

  public resetDisableWarning() {
    this._disableWarning = [];
  }

  public getDisabledWarning(): TreatmentWarning[] {
    return this._disableWarning;
  }
  public resetActiveChapter() {
    this._activeChapters = [];
  }

  public getActiveChapter(): ChapterSelector[] {
    return this._activeChapters;
  }


  /**
   *
   * @param chapter
   */
  public updateChapterInfo(chapter: GetChapterResponse) {
    this._chapterInfo = ChapterInfo.fromGetChapterResponse(chapter);
    this.chapterInfoSubject.next(this._chapterInfo);
  }

  public updateDateChapterInfo(lastUpdatedAt: number): void {
    if (this._chapterInfo) {
      this._chapterInfo.lastUpdatedAt = lastUpdatedAt;
    }
    this.chapterInfoSubject.next(this._chapterInfo);
  }


  /**
   * @param document
   */
  public updateDocumentInfo(document: DocumentInfo): void {
    this._document = document;
    this.docInfoSubject.next(this._document);
  }

  public updateDateDocumentInfo(updateAt: number) {
    if (this._document) {
      this._document = JSON.parse(JSON.stringify(this._document));
      this._document.updatedAt = updateAt;
      this.docInfoSubject.next(this._document);
    }
  }
}

